import {
  MAX_NUMBER_OF_UNITS,
  MAX_PRICE_PER_UNIT,
  MIN_NUMBER_OF_UNITS,
  MIN_PRICE_PER_UNIT,
  PRICE_DECIMAL_PLACES,
  VERSION_DETAILS_COPY,
  VERSION_DETAILS_FALLBACK
} from "../constants/versionDetails.constants";
import { VersionDetailsSummaryParts, VersionHistoryGroup } from "../types/versionHistory.types";

/**
 * Every formatter below tests `value == null` rather than truthiness: `0` units and a `0.00`
 * price are real, saved values and must never render as the "never set" dash.
 */
const isBlank = (raw: string): boolean => raw.trim() === "";

/** Commas are stripped first -- FormInputNumber's `formatWithCommas` puts them in the display value. */
const stripSeparators = (raw: string): string => raw.replace(/,/g, "").trim();

export const parseOptionalInteger = (raw: string): number | null => {
  if (isBlank(raw)) return null;
  const parsed = Number(stripSeparators(raw));
  return Number.isInteger(parsed) ? parsed : null;
};

export const parseOptionalDecimal = (raw: string): number | null => {
  if (isBlank(raw)) return null;
  const parsed = Number(stripSeparators(raw));
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatReportingYear = (value: number | null): string =>
  value == null ? VERSION_DETAILS_FALLBACK : String(value);

export const formatUnits = (value: number | null): string =>
  value == null ? VERSION_DETAILS_FALLBACK : value.toLocaleString("en-US");

/**
 * Deliberately not `Intl.NumberFormat({ style: "currency" })`: the company's stored
 * `alphabeticCode` is not guaranteed to be an ISO code `Intl` recognises, and an unknown code
 * makes Intl throw. A plain fixed-decimal number with the code alongside always renders.
 */
export const formatPricePerUnit = (value: number | null, currencyCode: string): string => {
  if (value == null) return VERSION_DETAILS_FALLBACK;
  const amount = value.toFixed(PRICE_DECIMAL_PLACES);
  return currencyCode ? `${currencyCode} ${amount}` : amount;
};

/** True once any of the three commercial-metadata fields has been saved for this version. */
export const hasVersionDetails = (
  version: Pick<VersionHistoryGroup, "reportingYear" | "numberOfUnits" | "pricePerUnit">
): boolean => version.reportingYear != null || version.numberOfUnits != null || version.pricePerUnit != null;

/** `null` when the version has no details at all, so the collapsed bar renders nothing. */
export const buildVersionDetailsSummaryParts = (
  version: Pick<VersionHistoryGroup, "reportingYear" | "numberOfUnits" | "pricePerUnit">,
  currencyCode: string
): VersionDetailsSummaryParts | null => {
  const { reportingYear, numberOfUnits, pricePerUnit } = version;
  if (!hasVersionDetails(version)) {
    return null;
  }
  return {
    year: formatReportingYear(reportingYear),
    units: formatUnits(numberOfUnits),
    price: formatPricePerUnit(pricePerUnit, currencyCode)
  };
};

/** Both validators accept empty -- these fields are optional and must stay clearable. */
export const validateNumberOfUnits = (raw: string | undefined): string | true => {
  if (raw === undefined || isBlank(raw)) return true;

  const parsed = Number(stripSeparators(raw));
  if (!Number.isInteger(parsed) || parsed < MIN_NUMBER_OF_UNITS) {
    return VERSION_DETAILS_COPY.unitsInvalid;
  }
  if (parsed > MAX_NUMBER_OF_UNITS) {
    return VERSION_DETAILS_COPY.unitsTooLarge;
  }
  return true;
};

export const validatePricePerUnit = (raw: string | undefined): string | true => {
  if (raw === undefined || isBlank(raw)) return true;

  const cleaned = stripSeparators(raw);
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < MIN_PRICE_PER_UNIT) {
    return VERSION_DETAILS_COPY.priceInvalid;
  }
  // Guards a paste; typing is already capped by FormInputNumber's enableLimitedDecimal.
  const decimals = cleaned.split(".")[1]?.length ?? 0;
  if (decimals > PRICE_DECIMAL_PLACES) {
    return VERSION_DETAILS_COPY.priceInvalid;
  }
  if (parsed > MAX_PRICE_PER_UNIT) {
    return VERSION_DETAILS_COPY.priceTooLarge;
  }
  return true;
};
